import { instance } from '@miotech/shared';
import { message } from 'antd';
import qs from 'qs';
import { getLocalStorage, USER_ROLE } from 'utils/storage';

export interface ResponseBody<T> {
  code: number;
  message: string;
  detailCode: string;
  result: T;
}
export interface BodyList<T> {
  total: number;
  pageNum: string;
  pageSize: string;
  data: T[];
}

export interface ResponseBodyList<T> extends ResponseBody<BodyList<T>> {}

export function urlBuilder(baseUrl: string): (path?: string, query?: Record<string, any>) => string {
  return function build(path, query) {
    let url = baseUrl;
    if (path) {
      url += path;
    }
    if (query) {
      url +=
        '?' +
        Object.entries(query)
          .reduce((accu, entry) => (entry[1] === undefined ? accu : accu + `${entry[0]}=${entry[1]}&`), '')
          .slice(0, -1);
    }
    return url;
  };
}

instance.interceptors.response.use(
  response => {
    if (response.status === 403 || response?.data?.code === 26) {
      return Promise.reject('response.data.message || ‘没有权限’');
    }
    if (response.data && response.data.type) {
      return Promise.resolve(response);
    }
    if (response.data && response.data.code !== 0) {
      if (response.data.detailCode === 'SYSTEM-USER-00001') {
        if (!window.location.pathname.includes('/change-password')) {
          message.error('您的密码已过期，请及时修改密码');
          window.location.href = '/gvi/change-password';
        }
        return Promise.reject(response.data);
      }
      if (
        response?.config.url?.includes('/v2/lqt-loan/carbon-emission-reduction/calculation') ||
        response?.config.url?.includes('/v2/project-loan/carbon-emission-reduction/calculation')
      ) {
        message.error('请检查并输入正确的数值');
      } else if (response?.config.url?.includes('/password/change') && response.data.code === 102) {
        message.error('新密码与原始密码重复');
      } else {
        message.error(response.data.message || response.data.note);
      }
      return Promise.reject(response.data.message || '数据结构异常');
    }
    return Promise.resolve(response);
  },
  error => {
    const errStr = String(error);
    if (errStr.indexOf('500') >= 0) {
      message.error('系统异常，请联系管理员');
      return Promise.reject();
    }
    if (errStr.indexOf('401') >= 0) {
      message.error('没有访问权限');
      return Promise.reject();
    }
    message.error('网络请求异常');
    return Promise.reject();
  },
);

export const connector = {
  get: async <T = any>(props: { url: string; data?: any }) => {
    const { url, data } = props;
    const res = await instance.get<ResponseBody<T>>(url, {
      params: data,
      method: 'get',
      headers: {
        'Role-Id': (getLocalStorage(USER_ROLE) as string) || '',
      },
      paramsSerializer: params =>
        qs.stringify(params, {
          arrayFormat: 'repeat',
          skipNulls: true,
        }),
    });
    return res.data;
  },
  post: async <T = any>(props: { url: string; data?: any; config?: any }) => {
    const { url, data, config } = props;
    const res = await instance.post<ResponseBody<T>>(url, data, {
      ...config,
      headers: {
        'Role-Id': (getLocalStorage(USER_ROLE) as string) || '',
        ...(config?.headers || {}),
      },
    });
    return res.data;
  },
  put: async <T = any>(props: { url: string; data?: any; config?: any }) => {
    const { url, data, config } = props;
    const res = await instance.put<ResponseBody<T>>(url, data, {
      ...config,
      headers: {
        'Role-Id': (getLocalStorage(USER_ROLE) as string) || '',
        ...(config?.headers || {}),
      },
    });
    return res.data;
  },
  delete: async <T = any>(props: { url: string; data?: any; config?: any }) => {
    const { url, data, config } = props;
    const res = await instance.delete<ResponseBody<T>>(url, {
      ...config,
      headers: {
        'Role-Id': (getLocalStorage(USER_ROLE) as string) || '',
      },
      data,
    });
    return res.data;
  },
};

export const propsWithLocale: <T>(props: T) => T & { locale: string } = props => ({
  ...props,
  locale: 'zh_CN',
});
